import type { ChannelInfoForList } from '@shared/api';
import { Table } from '@shared/ui/table';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Flex } from 'antd';
import { useHealthCheckChannel } from '@modules/channels/hooks/use-health-check-channel';
import { Text } from '@shared/ui/antd';
import { ChannelSettings } from '@shared/ui/channel-settings/channel-settings';
import { ChannelTypeIcon } from '../channel-type-icon/channel-type-icon';
import styles from './channels-table.module.scss';

interface ChannelsTableProps {
  channels?: ChannelInfoForList[];
  isToggleLoading: boolean;
  loadingChannelId: number | null;
  onToggleActive: (channelId: number, status: boolean) => void;
  onSelectChannelToUpdate: (channelId: number) => void;
  onDeleteChannel: (channelId: number) => void;
  onCopyWidgetScript: (channelId: number) => void;
}

export const ChannelsTable: FC<ChannelsTableProps> = ({
  channels,
  isToggleLoading,
  loadingChannelId,
  onToggleActive,
  onSelectChannelToUpdate,
  onDeleteChannel,
  onCopyWidgetScript,
}) => {
  const { t: tTable } = useTranslation('translation', { keyPrefix: 'channels.table' });
  const { t: tChannels } = useTranslation('translation', { keyPrefix: 'channels' });

  const {
    healthCheckChannel,
    isLoading: isHealthCheckLoading,
    loadingChannelId: healthCheckId,
  } = useHealthCheckChannel();

  return (
    <Table>
      <Table.THead isSticky>
        <Table.THeadCellText width={260}>{tTable('type')}</Table.THeadCellText>
        <Table.THeadCellText width={320}>{tTable('name')}</Table.THeadCellText>
        <Table.THeadCellText width={200}>{tTable('aiAgentName')}</Table.THeadCellText>
        <Table.THeadCellText width={140}>{tTable('status')}</Table.THeadCellText>
        <Table.THeadCellText
          width={100}
          style={{ textAlign: 'center' }}
          textStyle={{ textAlign: 'center' }}
        >
          {tTable('actions')}
        </Table.THeadCellText>
      </Table.THead>
      <Table.TBody>
        {channels &&
          channels.map((channel) => (
            <Table.TRow key={channel.id}>
              <Table.TCell>
                <Flex align="center" gap={12}>
                  <ChannelTypeIcon type={channel.type} />
                  <Text variant="sm" fontWeight={400}>
                    {tChannels(`channelType.${channel.type}`)}
                  </Text>
                </Flex>
              </Table.TCell>
              <Table.TCellText variant="sm" fontWeight={400}>
                {channel.name || '-'}
              </Table.TCellText>
              <Table.TCellText variant="sm" fontWeight={400}>
                {channel.agent_name || '-'}
              </Table.TCellText>
              <Table.TCell>
                <Text
                  variant="sm"
                  fontWeight={400}
                  className={channel.is_active ? styles.statusActive : styles.statusInactive}
                >
                  {channel.is_active ? tTable('statusActive') : tTable('statusInactive')}
                </Text>
              </Table.TCell>
              <Table.TCell key="actions" align="center" style={{ padding: 0 }}>
                <Flex align="center" justify="center">
                  <ChannelSettings
                    channelId={channel.id}
                    isActive={channel.is_active}
                    isToggleLoading={isToggleLoading && loadingChannelId === channel.id}
                    isHealthCheckLoading={isHealthCheckLoading && healthCheckId === channel.id}
                    onToggleActive={(status) => onToggleActive(channel.id, status)}
                    onHealthCheck={() => healthCheckChannel(channel.id)}
                    onSelectChannelToUpdate={onSelectChannelToUpdate}
                    onDeleteChannel={onDeleteChannel}
                    onCopyScript={
                      channel.type === 'widget' ? () => onCopyWidgetScript(channel.id) : undefined
                    }
                  />
                </Flex>
              </Table.TCell>
            </Table.TRow>
          ))}
      </Table.TBody>
    </Table>
  );
};
